import { gql, useQuery } from '@apollo/client';
import type { Theme } from '@material-ui/core';
import { Tooltip, withStyles } from '@material-ui/core';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import type { FC } from 'react';
import { useContext, useEffect, useState } from 'react';

import { ModalStateContext } from '../../contexts/app-state-context/modal-state.context';
import { useWeb3State } from '../../hooks';
import type { Asset } from '../../lib/graph/MogulMarketplace';
import { collectionHasUnlockableContent } from '../../utils/collection.util';
import { UnlockableContentModal } from '../common/modal/unlockable-content/UnlockableContentModal.component';

const IS_OWNER_QUERY = gql`
  query Asset($graphId: String!, $addresses: [String]) {
    asset(id: $graphId) {
      id
      owners(where: { quantity_gt: 0, account_in: $addresses }) {
        account {
          address
        }
        quantity
      }
    }
  }
`;

interface UnlockableContentProps {
  nftContractAddress: string;
  nftId: string;
}

const LockableContentToolTip = withStyles((theme: Theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.black,
    fontSize: 14,
  },
}))(Tooltip);

export const UnlockableContent: FC<UnlockableContentProps> = ({
  nftContractAddress,
  nftId,
}) => {
  const { setModal } = useContext(ModalStateContext);
  const { walletDetails, isInitialized } = useWeb3State();

  const graphId = `${nftContractAddress}/${nftId}`;
  const walletAddress = walletDetails.address?.toLowerCase();
  const addresses = [walletAddress ?? ''];

  // TODO: fix query after refactor
  const { loading, data } = useQuery<{ asset: Asset }>(IS_OWNER_QUERY, {
    variables: { graphId, addresses },
    pollInterval: 1000,
  });

  const isUnlocked =
    !loading &&
    walletAddress ===
      (data?.asset?.owners && data?.asset?.owners.length > 0
        ? data?.asset?.owners[0].account.address
        : '');

  const [hasUnlockableContent, setHasUnlockableContent] = useState(false);

  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    void (async () => {
      const unlockableContent = await collectionHasUnlockableContent(
        nftContractAddress,
        nftId,
      );

      setHasUnlockableContent(unlockableContent);
    })();
  }, [isInitialized]);

  const openUnlockableContent = () => {
    setModal(
      UnlockableContentModal({
        tokenAddress: nftContractAddress,
        tokenId: nftId,
      }),
      false,
    );
  };

  const lockedToolTipText =
    'Only the owner can view the content. If you are the owner, please delist the Item';
  return hasUnlockableContent ? (
    <div className="my-5 w-full">
      <div className="w-full grid place-items-center">
        <div className="flex items-center w-full h-10">
          <button
            className="w-full h-full border-2 border-solid border-white rounded flex items-center justify-center hover:opacity-70 disabled:opacity-50"
            onClick={openUnlockableContent}
            disabled={!isUnlocked}
          >
            <LockableContentToolTip
              disableHoverListener={isUnlocked}
              title={lockedToolTipText}
              placement="top"
              className="cursor-pointer"
            >
              <div className="w-full flex items-center justify-center">
                {isUnlocked ? (
                  <LockOpenIcon className="mr-2" />
                ) : (
                  <LockOutlinedIcon className="mr-2" />
                )}
                Reveal unlockable content
              </div>
            </LockableContentToolTip>
          </button>
        </div>
      </div>
    </div>
  ) : null;
};
